import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const IndustryShowcase = ({ industries, onIndustrySelect }) => {
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const handleIndustryClick = (industry) => {
    setSelectedIndustry(industry?.id === selectedIndustry?.id ? null : industry);
    if (onIndustrySelect) {
      onIndustrySelect(industry?.id === selectedIndustry?.id ? null : industry);
    }
  };

  const getIndustryIcon = (industryName) => {
    const icons = {
      'Manufacturing': 'Factory',
      'Financial Services': 'Building2',
      'Healthcare': 'Heart',
      'Technology': 'Cpu',
      'Retail': 'ShoppingBag',
      'Energy': 'Zap',
      'Automotive': 'Car',
      'Telecommunications': 'Radio'
    };
    return icons?.[industryName] || 'Building';
  };

  return (
    <div className="bg-white rounded-lg enterprise-shadow enterprise-border p-6 mb-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          Industry Expertise
        </h2>
        <p className="text-slate-700">
          Specialized AI solutions across diverse sectors
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {industries?.map((industry, index) => (
          <div
            key={index}
            onClick={() => handleIndustryClick(industry)}
            className={`p-4 rounded-lg border-2 cursor-pointer enterprise-transition group ${
              selectedIndustry?.id === industry?.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-surface'
            }`}
          >
            <div className="text-center">
              <div className={`w-12 h-12 mx-auto rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 enterprise-transition ${
                selectedIndustry?.id === industry?.id
                  ? 'bg-primary text-white' :'bg-surface text-primary'
              }`}>
                <Icon name={getIndustryIcon(industry?.name)} size={24} />
              </div>
              <h3 className={`font-medium mb-1 ${
                selectedIndustry?.id === industry?.id
                  ? 'text-primary' :'text-slate-900'
              }`}>
                {industry?.name}
              </h3>
              {/* Case study count removed per request */}
              <div className="text-sm font-medium text-success">
                Avg. {industry?.avgROI} ROI
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Selected Industry Details */}
      {selectedIndustry && (
        <div className="bg-surface rounded-lg p-6 enterprise-transition">
          <div className="flex items-start space-x-4 mb-4">
            <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center">
              <Icon name={getIndustryIcon(selectedIndustry?.name)} size={32} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {selectedIndustry?.name} Solutions
              </h3>
              <p className="text-slate-700 leading-relaxed">
                {selectedIndustry?.description}
              </p>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {selectedIndustry?.keyMetrics?.map((metric, index) => (
              <div key={index} className="text-center bg-white rounded-lg p-3">
                <div className="text-lg font-bold text-primary mb-1">
                  {metric?.value}
                </div>
                <div className="text-xs text-slate-600">
                  {metric?.label}
                </div>
              </div>
            ))}
          </div>

          {/* Common Use Cases */}
          <div className="mb-4">
            <h4 className="font-medium text-slate-900 mb-3">Common Use Cases</h4>
            <div className="flex flex-wrap gap-2">
              {selectedIndustry?.useCases?.map((useCase, index) => (
                <span key={index} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                  {useCase}
                </span>
              ))}
            </div>
          </div>

          {/* Featured Clients */}
          {selectedIndustry?.featuredClients && (
            <div>
              <h4 className="font-medium text-slate-900 mb-3">Featured Clients</h4>
              <div className="flex items-center space-x-4">
                {selectedIndustry?.featuredClients?.map((client, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2">
                    <Image 
                      src={client?.logo} 
                      alt={`${client?.name} logo`}
                      className="w-6 h-6 object-contain"
                    />
                    <span className="text-sm text-slate-600">{client?.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IndustryShowcase;